// Single source of truth for stale-chunk recovery after a frontend deploy.
//
// When a new build ships, hashed chunk filenames change (e.g.
// `SignupPage-vDpAc7hm.js` -> `SignupPage-a9Xk2p1z.js`). A tab that already
// loaded the previous `index.html` still references the old hashes, so any
// dynamic import or CSS preload for that chunk 404s. This module is the ONE
// detector + ONE policy + ONE actuator all call sites funnel into:
//   - isStaleChunkError()  -> classify an error
//   - markBuildStale()     -> record an out-of-band signal (backend build-id poll)
//   - requestReload()      -> decide + perform the reload, loop-proofed
//
// Call sites: vite:preloadError handler and Sentry.ErrorBoundary fallback in
// index.tsx, lazyWithReload.ts, and components/ErrorBoundary/ErrorBoundary.tsx.
import { isElectron } from './electron';

const STORAGE_KEY = 'chunkRecovery';

// After this many reload attempts within the window, stop reloading and let
// the caller render a "new version available" prompt instead of looping.
// Two attempts covers both the common paths (vite:preloadError firing first,
// then a lazyWithReload/ErrorBoundary catch a moment later) as one incident.
const MAX_RELOAD_ATTEMPTS = 2;

// A fresh incident (first attempt in the last 5 minutes) resets the counter.
// This keeps a tab left open across multiple future deploys self-healing —
// each new deploy gets its own reload budget — while still bounding retries
// within a single incident if chunks are genuinely missing (partial outage).
const RECOVERY_WINDOW_MS = 5 * 60_000;

const CHUNK_LOAD_PATTERNS = [
  // React.lazy() default-export access on a chunk that failed to load.
  '_result.default',
  "'default'",
  '"default"',
  // Vite dynamic import / CSS preload failures.
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Loading chunk',
  'Unable to preload CSS',
  'dynamically imported module',
];

interface RecoveryState {
  attempts: number;
  firstAt: number;
  buildId?: string;
}

/** True when an error's message matches a known stale-chunk signature. */
export function isStaleChunkError(error: unknown): boolean {
  if (!error) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return CHUNK_LOAD_PATTERNS.some((pattern) => msg.includes(pattern));
}

// Set by index.tsx's build-freshness poller when the deployed backend build
// ID diverges from the one baked into this bundle.
let _staleBuild = false;

export function markBuildStale(): void {
  _staleBuild = true;
}

export function isBuildStale(): boolean {
  return _staleBuild;
}

function readState(): RecoveryState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.attempts === 'number' && typeof parsed?.firstAt === 'number') {
      return parsed as RecoveryState;
    }
    return null;
  } catch {
    return null;
  }
}

function writeState(state: RecoveryState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable (Safari private mode) — reload proceeds
    // below without a persisted guard; worst case this tab loses loop
    // protection for the remainder of the session.
  }
}

/**
 * The single actuator for stale-chunk recovery. Every detector funnels into
 * this function rather than calling `window.location.reload()` directly.
 *
 * Returns `true` if a reload was triggered. Returns `false` when recovery is
 * exhausted (caller should render a "new version available" fallback instead
 * of looping) or when running inside Electron, where the app loads bundled
 * local assets and there is no deploy skew to recover from.
 */
export function requestReload(reason: string): boolean {
  if (typeof window === 'undefined') return false;
  if (isElectron()) return false;

  const buildId = (import.meta.env.VITE_BUILD_ID as string) || '';
  const now = Date.now();
  const prev = readState();

  // A persisted buildId that differs from the current one means this tab
  // already reloaded once and landed on a genuinely new deploy — that's a
  // fresh incident, not a loop, so its budget resets regardless of window.
  const buildChanged = !!(prev?.buildId && buildId && prev.buildId !== buildId);
  const withinWindow = !!prev && now - prev.firstAt < RECOVERY_WINDOW_MS;
  const isFreshIncident = buildChanged || !withinWindow;

  const attempts = isFreshIncident ? 0 : prev!.attempts;
  if (attempts >= MAX_RELOAD_ATTEMPTS) return false;

  writeState({
    attempts: attempts + 1,
    firstAt: isFreshIncident ? now : prev!.firstAt,
    buildId,
  });

  if (import.meta.env.MODE !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[chunkRecovery] reloading (reason=${reason}, attempt=${attempts + 1}/${MAX_RELOAD_ATTEMPTS})`);
  }

  window.location.reload();
  return true;
}

/** True once `requestReload` has exhausted its budget for the current incident. */
export function isRecoveryExhausted(): boolean {
  const prev = readState();
  if (!prev) return false;
  const withinWindow = Date.now() - prev.firstAt < RECOVERY_WINDOW_MS;
  return withinWindow && prev.attempts >= MAX_RELOAD_ATTEMPTS;
}

/** Test-only: clears in-memory + persisted state between test cases. */
export function _resetForTests(): void {
  _staleBuild = false;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
